// ══════════════════════════════════════════════════════════════════════════════
// cp_polozka_v1 (Mirko 20. 10. 2026): JEDEN dialog položky katalógu CP pre celý Matrix (ústava 0.J).
// Používa ho Katalógy → Položky CP (nová / upraviť) aj Cenová ponuka („+ Nová položka“ a „Uložiť do katalógu“
// pri starej vlastnej položke). Nová položka vzniká VŽDY v katalógu — mimo katalógu už žiadna položka nevzniká.
// Obrázok je záznam v registri príloh (attachments) a dá sa vybrať z už nahratých (spoločný pre viac položiek).
// Presunuté z views/CpKatalogView.jsx (pôvodne ItemModal), nie kópia.
// ══════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useToast } from './Toast.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { BtnAdd, BtnGhost } from './Btn.jsx'
import { Modal } from './Modal.jsx'
import { useLock } from '../hooks/useLock.js'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'
import NavIcon from './NavIcon.jsx'

export const TRADES = [
  { key: 'omietky',    label: 'Omietky',    color: '#22c55e' },
  { key: 'potery',     label: 'Potery',     color: '#f97316' },
  { key: 'maliarske',  label: 'Maľovanie',  color: '#ec4899' },
  { key: 'zateplenie', label: 'Zateplenie', color: '#3b82f6' },
  { key: 'ine',        label: 'Iné',        color: '#64748b' },
]

export default function KatalogPolozkaModal({ record, onClose, onSaved, predloha = null, zPonuky = false, naviazat = false }) {
  const isEdit = !!record
  const src = record || predloha || {}   // predloha = hodnoty z riadku CP, ktory sa zaraduje do katalogu
  const [jednorazova, setJednorazova] = useState(false)
  const [spolocne, setSpolocne] = useState([])   // uz nahrate obrazky katalogu — spolocny register, jeden obrazok moze mat viac poloziek
  useEffect(() => { fetchAuth('/api/quote-catalog').then(r => r.ok ? r.json() : []).then(d => {
    const videne = new Set(); setSpolocne((Array.isArray(d) ? d : []).filter(c => c.image && !videne.has(c.image) && videne.add(c.image)))
  }).catch(() => {}) }, [])
  const { user } = useAuth()
  // P1.2_USELOCK_2026-05-30: zámok cp-katalóg položky pri editácii
  const { lockInfo, acquired } = useLock(isEdit ? 'quote_catalog' : null, isEdit ? record?.id : null, user)
  const isLocked = isEdit && !acquired && !!lockInfo?.by
  const [form, setForm] = useState({
    title: src.title || '',
    full_name: src.full_name || '',
    trade: src.trade || src.category || 'omietky',
    category: src.category || src.trade || 'omietky',
    unit: src.unit || 'm²',
    price: src.price || 0,
    labor_price: src.labor_price || 0,
    material_price: src.material_price || 0,
    material: src.material || '',
    thickness: src.thickness || '',
    consumption: src.consumption || '',
    description: src.description || '',
    skladba: src.skladba || '',
    base_thickness_mm: src.base_thickness_mm || 0,
    base_consumption_val: src.base_consumption_val || 0,
    auto_consumption: src.auto_consumption === 0 ? 0 : 1, // Mirko 16.8. — checkbox automatiky spotreby
    // Mirko 2026-05-27 — calc_mode (ústava 21.6.A)
    material_calc_mode: src.material_calc_mode || 'fixed', // 'fixed' | 'kg' | 'm3'
    consumption_kg_per_m2_per_mm: src.consumption_kg_per_m2_per_mm || 0,
    default_thickness_mm: src.default_thickness_mm || 0,
    suppress_vop_ids: src.suppress_vop_ids || '[]', // Mirko 2026-07-15 — skryva priplatky pri vlozeni do CP
    active: src.active ?? 1,
    image: src.image || '',   // cp_polozka_v1: spolocny obrazok z registra
  })
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(src.image ? `/uploads/${src.image}` : null)
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  // DIRTY GUARD (ustava 53.1, Mirko 2026-07-16)
  const initFormRef = useRef(JSON.stringify(form))
  const isDirty = JSON.stringify(form) !== initFormRef.current || !!imageFile
  const [dirtyDialog, setDirtyDialog] = useState(false)

  // Priplatky ciselnika — na vazbu "Skryva priplatky" (Mirko 2026-07-15)
  const [vopPriplatky, setVopPriplatky] = useState([])
  useEffect(() => {
    fetchAuth('/api/vop-items').then(r => r.ok ? r.json() : []).then(d =>
      setVopPriplatky((Array.isArray(d) ? d : []).filter(v => v.category === 'priplatky' && v.active !== 0))
    ).catch(() => {})
  }, [])

  function upd(k, v) { setForm(prev => ({ ...prev, [k]: v })) }

  const _s0 = useRef(null); if (_s0.current === null) _s0.current = JSON.stringify(form); useDirtyFlag(JSON.stringify(form) !== _s0.current, { key: 'cpkatalog-polozka', discard: () => { onClose && onClose() } })   // straz_v4
  async function save() {
    if (!form.title.trim()) { toast.error('Názov je povinný'); return }
    setBusy(true)
    try {
      const url = isEdit ? `/api/quote-catalog/${record.id}` : '/api/quote-catalog'
      const telo = isEdit ? form : { ...form, active: jednorazova ? 0 : 1 }   // cp_polozka_v1
      const method = isEdit ? 'PUT' : 'POST'
      const r = await fetchAuth(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(telo),
      })
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || ('uloženie zlyhalo (HTTP ' + r.status + ')')) }
      const saved = await r.json()
      // Upload image ak je vybraná
      if (imageFile) {
        const fd = new FormData()
        fd.append('image', imageFile)
        const ri = await fetchAuth(`/api/quote-catalog/${saved.id}/image`, { method: 'POST', body: fd })
        const di = await ri.json().catch(() => ({}))
        if (!ri.ok) toast.error('Položka je uložená, ale obrázok sa nenahral: ' + (di.error || ('HTTP ' + ri.status)))
        else saved.image = di.filename || saved.image
      }
      toast.success(isEdit ? 'Položka upravená' : (jednorazova ? 'Položka uložená do katalógu (archív — neponúka sa)' : 'Položka uložená do katalógu'))
      onSaved(saved)
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }

  const inp = {
    width: '100%', padding: '8px 10px',
    border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm,
    fontSize: TYPO.sizes.sm, background: COLORS.bg, boxSizing: 'border-box',
  }
  const lbl = { fontSize: TYPO.sizes.xs, color: COLORS.muted, fontWeight: TYPO.weights.semibold,
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }

  return (
    <Modal open title={isEdit ? `Upraviť ${record.title}` : (naviazat ? 'Uložiť položku do katalógu' : (zPonuky ? 'Nová položka — do katalógu a do ponuky' : 'Nová položka katalógu'))} onClose={onClose} maxWidth={760}
      isDirty={isDirty} onDirtyClose={() => setDirtyDialog(true)}>
      {/* P1.2_USELOCK_2026-05-30: zámok banner */}
      {isLocked && (
        <div style={{ background: '#fef2f2', border: `1px solid #ef4444`, borderRadius: RADIUS.md,
          padding: SPACE.sm, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm, color: '#b91c1c' }}>
          Položku katalógu práve upravuje <strong>{lockInfo.by}</strong>. Uloženie je zablokované.
        </div>
      )}
      {zPonuky && !isEdit && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: RADIUS.md, padding: SPACE.sm, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm }}>
          {naviazat
            ? <>Položka sa uloží do <b>katalógu</b> (Katalógy → Položky CP) a tento riadok ponuky sa na ňu naviaže — texty riadku sa prevezmú odtiaľto, množstvo a cena ostanú. Nový riadok nevznikne.</>
            : <>Položka sa uloží do <b>katalógu</b> (Katalógy → Položky CP) a hneď sa vloží do tejto ponuky — nabudúce ju nájde každý cez „+ Pridať z katalógu“.</>}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={jednorazova} onChange={e => setJednorazova(e.target.checked)} />
            <span>Jednorazová práca — v katalógu ostane v archíve a nebude sa ponúkať (v ponuke ostáva)</span>
          </label>
        </div>
      )}
      <fieldset disabled={isLocked} style={{ border: 'none', padding: 0, margin: 0,
        opacity: isLocked ? 0.6 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}>
      {/* cp_polozka_v1 (Mirko 20. 10.): živý náhľad — človek vidí, ktorý riadok je ktorý, skôr než uloží */}
      <div style={{ ...lbl, marginBottom: 4 }}>Takto to bude v ponuke</div>
      <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr auto', gap: SPACE.sm, alignItems: 'center', padding: '8px 10px', marginBottom: SPACE.md,
        border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, background: COLORS.bgSoft, fontSize: TYPO.sizes.sm }}>
        <div style={{ width: 44, height: 44, borderRadius: RADIUS.sm, border: `1px dashed ${COLORS.line}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.bg }}>
          {imagePreview ? <img src={imagePreview} alt="" style={{ width: 44, height: 44, objectFit: 'cover' }} /> : <NavIcon name="fotky" size={16} />}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: TYPO.weights.semibold }}>{form.title || <span style={{ color: COLORS.muted }}>Názov položky</span>}</div>
          {form.full_name ? <div style={{ fontSize: TYPO.sizes.xs, marginTop: 2, whiteSpace: 'pre-wrap' }}>{form.full_name}</div> : null}
          {form.material ? <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontStyle: 'italic' }}>{form.material}</div> : null}
          {form.description ? <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 3, whiteSpace: 'pre-wrap' }}>{form.description}</div> : null}
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
          <div>{form.thickness || '—'}{form.consumption ? <><br />{form.consumption}</> : null}</div>
          <div style={{ color: COLORS.text, fontWeight: TYPO.weights.semibold, marginTop: 2 }}>{(Number(form.price) || 0).toFixed(2).replace('.', ',')} € / {form.unit || 'm²'}</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: SPACE.md }}>
        {/* Obrázok */}
        <div>
          <div style={lbl}>Obrázok</div>
          <div style={{
            width: 180, height: 180, background: COLORS.bgSoft,
            border: `1px dashed ${COLORS.line}`, borderRadius: RADIUS.md,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', marginBottom: SPACE.sm,
          }}>
            {imagePreview
              ? <img src={imagePreview} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <span style={{ color: COLORS.muted, display: 'flex' }}><NavIcon name="fotky" size={32} /></span>}
          </div>
          <label style={{
            display: 'block', textAlign: 'center', padding: '6px 12px',
            background: COLORS.nav, color: '#fff', borderRadius: RADIUS.sm,
            cursor: 'pointer', fontSize: TYPO.sizes.sm,
          }}>
            Vybrať obrázok…
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0]
                if (!f) return
                setImageFile(f)
                setImagePreview(URL.createObjectURL(f))
                upd('image', '')   // cp_polozka_v1: subor ma prednost, kliknuty spolocny obrazok sa zrusi
              }} />
          </label>
          {spolocne.length > 0 && (
            <div style={{ marginTop: SPACE.sm }}>
              <div style={{ ...lbl, marginBottom: 4 }}>Alebo z už nahratých</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 150, overflow: 'auto' }}>
                {spolocne.map(c => (
                  <img key={c.image} src={`/uploads/${c.image}`} alt="" title={c.title}
                    onClick={() => { setImageFile(null); setImagePreview(`/uploads/${c.image}`); upd('image', c.image) }}
                    style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: RADIUS.sm, cursor: 'pointer',
                      border: `2px solid ${form.image === c.image ? COLORS.nav : COLORS.line}` }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Formulár */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm }}>
          <label>
            <div style={lbl}>Názov * <span style={{ textTransform: 'none', fontWeight: 400 }}>— 1. riadok, tučne (ponuka aj PDF)</span></div>
            <input style={inp} value={form.title} onChange={e => upd('title', e.target.value)}
              placeholder="Sadrová omietka stien…" autoFocus />
          </label>
          <label>
            <div style={lbl}>Plný názov <span style={{ textTransform: 'none', fontWeight: 400 }}>— 2. riadok pod názvom (napr. obchodné označenie)</span></div>
            <input style={inp} value={form.full_name} onChange={e => upd('full_name', e.target.value)}
              placeholder="Jednovrstvová sadrová omietka pod obchodným označením…" />
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: SPACE.sm }}>
            <label>
              <div style={lbl}>Remeslo</div>
              <select style={inp} value={form.trade}
                onChange={e => { upd('trade', e.target.value); upd('category', e.target.value) }}>
                {TRADES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <label>
              <div style={lbl}>MJ <span style={{ textTransform: 'none', fontWeight: 400 }}>— jednotka ceny</span></div>
              <select style={inp} value={form.unit} onChange={e => upd('unit', e.target.value)}>
                <option value="m²">m²</option>
                <option value="m">m</option>
                <option value="bm">bm (bežný meter)</option>
                <option value="m³">m³</option>
                <option value="ks">ks</option>
                <option value="bal">bal</option>
                <option value="hod">hod</option>
                <option value="kg">kg</option>
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: SPACE.sm }}>
            <label>
              <div style={lbl}>Cena celkom (€)</div>
              <input style={inp} type="number" step="0.01" value={form.price}
                onChange={e => upd('price', Number(e.target.value) || 0)} />
            </label>
            <label>
              <div style={lbl}>Cena za realizáciu (€)</div>
              <input style={inp} type="number" step="0.01" value={form.labor_price}
                onChange={e => upd('labor_price', Number(e.target.value) || 0)} />
            </label>
            <label>
              <div style={lbl}>Cena za materiál (€)</div>
              <input style={inp} type="number" step="0.01" value={form.material_price}
                onChange={e => upd('material_price', Number(e.target.value) || 0)} />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
            <label>
              <div style={lbl}>Materiál <span style={{ textTransform: 'none', fontWeight: 400 }}>— 3. riadok, kurzívou (značka)</span></div>
              <input style={inp} value={form.material} onChange={e => upd('material', e.target.value)}
                placeholder="Baumit RatioGlatt L" />
            </label>
            <label>
              <div style={lbl}>Hrúbka <span style={{ textTransform: 'none', fontWeight: 400 }}>— popis hrúbky do PDF (napr. 15 mm); číslo v mm sa zadáva v riadku ponuky</span></div>
              <input style={inp} value={form.thickness} onChange={e => upd('thickness', e.target.value)}
                placeholder="15 mm v cene" />
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
            <label>
              <div style={lbl}>Spotreba <span style={{ textTransform: 'none', fontWeight: 400 }}>— text pod hrúbkou (napr. cca 4,0 kg/m² v cene)</span></div>
              <input style={inp} value={form.consumption} onChange={e => upd('consumption', e.target.value)}
                placeholder="cca 12 kg/m² na 15 mm" />
            </label>
            <label>
              <div style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={Number(form.auto_consumption) !== 0}
                  onChange={e => upd('auto_consumption', e.target.checked ? 1 : 0)} style={{ margin: 0 }} />
                Automatický prepočet spotreby podľa hrúbky
              </div>
              <div style={{ display: 'flex', gap: 4, opacity: Number(form.auto_consumption) !== 0 ? 1 : 0.45 }}>
                <input style={{ ...inp, flex: 1 }} type="number" step="0.1" value={form.base_thickness_mm}
                  onChange={e => upd('base_thickness_mm', Number(e.target.value) || 0)} placeholder="mm" title="Hrúbka omietky (mm)" />
                <input style={{ ...inp, flex: 1 }} type="number" step="0.01" value={form.base_consumption_val}
                  onChange={e => upd('base_consumption_val', Number(e.target.value) || 0)} placeholder="kg/m²" title="Spotreba (kg/m²) pri tejto hrúbke" />
              </div>
              <div style={{ fontSize: 10, color: '#92400e', marginTop: 2, fontFamily: 'monospace' }}>
                {form.base_thickness_mm > 0 && form.base_consumption_val > 0
                  ? `= ${(form.base_consumption_val / form.base_thickness_mm).toFixed(2)} kg/m² na 1 mm — CP dopočíta pre zadanú hrúbku`
                  : 'zadaj hrúbku (mm) + spotrebu (kg/m²) pri nej — CP dopočíta pre inú hrúbku'}
                {Number(form.auto_consumption) === 0 ? ' · VYPNUTÉ — spotreba sa píše ručne v CP' : ''}
              </div>
            </label>
          </div>

          {/* ─── Druh prepočtu materiálu (Mirko 2026-05-27, ústava 21.6.A) ─── */}
          <div style={{
            background: COLORS.bgSoft, border: '1px solid rgba(29,78,216,0.35)',
            borderRadius: 8, padding: SPACE.sm, marginTop: SPACE.sm,
          }}>
            <div style={{ ...lbl, marginBottom: 6, color: '#92400e', fontWeight: 700 }}>
              Druh prepočtu materiálu (CP vzorec)
            </div>
            <select value={form.material_calc_mode} onChange={e => upd('material_calc_mode', e.target.value)}
              style={{ ...inp, marginBottom: 8 }}>
              <option value="fixed">Pevná cena (€/m²) — žiadny prepočet</option>
              <option value="kg">Sádra / vápenno-cement (€/tona × kg/m²/mm × hrúbka)</option>
              <option value="m3">Anhydrit / poter (€/m³ × hrúbka)</option>
            </select>

            {form.material_calc_mode === 'fixed' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: SPACE.sm }}>
                <label>
                  <div style={lbl}>Cena materiálu (€/m²)</div>
                  <input style={inp} type="number" step="0.01" value={form.material_price}
                    onChange={e => upd('material_price', Number(e.target.value) || 0)}
                    placeholder="napr. 2.50" />
                </label>
              </div>
            )}

            {form.material_calc_mode === 'kg' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: SPACE.sm }}>
                <label>
                  <div style={lbl}>Cena za tonu (€/t)</div>
                  <input style={inp} type="number" step="1" value={form.material_price}
                    onChange={e => upd('material_price', Number(e.target.value) || 0)}
                    placeholder="napr. 400" />
                </label>
                <label>
                  <div style={lbl}>Spotreba (kg/m²/mm)</div>
                  <input style={inp} type="number" step="0.1" value={form.consumption_kg_per_m2_per_mm}
                    onChange={e => upd('consumption_kg_per_m2_per_mm', Number(e.target.value) || 0)}
                    placeholder="napr. 1.2" title="kg/m² na 1 mm hrúbky (z tech. listu výrobcu)" />
                </label>
                <label>
                  <div style={lbl}>Default hrúbka (mm)</div>
                  <input style={inp} type="number" step="1" value={form.default_thickness_mm}
                    onChange={e => upd('default_thickness_mm', Number(e.target.value) || 0)}
                    placeholder="15" />
                </label>
              </div>
            )}

            {form.material_calc_mode === 'm3' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
                <label>
                  <div style={lbl}>Cena za m³ (€/m³)</div>
                  <input style={inp} type="number" step="1" value={form.material_price}
                    onChange={e => upd('material_price', Number(e.target.value) || 0)}
                    placeholder="napr. 230" />
                </label>
                <label>
                  <div style={lbl}>Default hrúbka (mm)</div>
                  <input style={inp} type="number" step="1" value={form.default_thickness_mm}
                    onChange={e => upd('default_thickness_mm', Number(e.target.value) || 0)}
                    placeholder="55" />
                </label>
              </div>
            )}

            {/* Live preview prepočtu */}
            {form.material_calc_mode !== 'fixed' && form.default_thickness_mm > 0 && form.material_price > 0 && (() => {
              const t = Number(form.default_thickness_mm)
              const p = Number(form.material_price)
              let preview = 0
              if (form.material_calc_mode === 'kg') {
                preview = Number(form.consumption_kg_per_m2_per_mm) * t * (p / 1000)
              } else if (form.material_calc_mode === 'm3') {
                preview = p * (t / 1000)
              }
              return (
                <div style={{
                  marginTop: 8, padding: '6px 10px',
                  background: '#dcfce7', border: '1px solid #22c55e', borderRadius: 6,
                  fontSize: TYPO.sizes.xs, color: '#15803d', fontFamily: TYPO.mono,
                }}>
                  ✓ Pri default hrúbke {t} mm → materiál <b>{preview.toFixed(2)} €/m²</b>
                </div>
              )
            })()}
          </div>
          <div>
            <div style={lbl}>Skrýva príplatky (pri vložení položky do ponuky sa automaticky odškrtnú)</div>
            {(() => {
              let sel = []
              try { sel = JSON.parse(form.suppress_vop_ids || '[]') } catch (_) {}
              if (!Array.isArray(sel)) sel = []
              const opts = vopPriplatky.filter(v => v.trade === form.trade || v.trade === 'spolocne')
              if (!opts.length) return <div style={{ fontSize: 12, color: '#9ca3af' }}>Žiadne príplatky pre toto remeslo</div>
              return opts.map(v => (
                <label key={v.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, padding: '3px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={sel.includes(v.id)}
                    onChange={() => upd('suppress_vop_ids', JSON.stringify(sel.includes(v.id) ? sel.filter(x => x !== v.id) : [...sel, v.id]))} />
                  <span>{v.text}</span>
                </label>
              ))
            })()}
          </div>
          <label>
            <div style={lbl}>Popis <span style={{ textTransform: 'none', fontWeight: 400 }}>— drobný text pod položkou (ponuka aj PDF)</span></div>
            <textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={form.description}
              onChange={e => upd('description', e.target.value)}
              placeholder="Strojovo spracovateľná vnútorná omietka…" />
          </label>
          <label>
            <div style={lbl}>Skladba / technický popis <span style={{ textTransform: 'none', fontWeight: 400 }}>— interné, zákazník to nevidí</span></div>
            <textarea style={{ ...inp, resize: 'vertical' }} rows={3} value={form.skladba}
              onChange={e => upd('skladba', e.target.value)}
              placeholder="Vrstva 1: penetrácia…&#10;Vrstva 2: jadrová omietka…" />
          </label>
        </div>
      </div>
      <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end', marginTop: SPACE.md, paddingTop: SPACE.md, borderTop: `1px solid ${COLORS.line}` }}>
        <BtnGhost onClick={() => isDirty ? setDirtyDialog(true) : onClose()} disabled={busy}>Zrušiť</BtnGhost>
        <BtnAdd onClick={save} disabled={busy || isLocked}>{busy ? 'Ukladám…' : (isLocked ? 'Zamknuté' : (isEdit ? '✓ Uložiť zmeny' : (naviazat ? '+ Uložiť do katalógu a naviazať riadok' : (zPonuky ? '+ Uložiť do katalógu a vložiť do ponuky' : '+ Pridať položku'))))}</BtnAdd>
      </div>
      <Modal.DirtyConfirm open={dirtyDialog}
        onSave={() => { setDirtyDialog(false); save() }}
        onContinue={() => setDirtyDialog(false)}
        onDiscard={() => { setDirtyDialog(false); onClose() }} />
      </fieldset>
    </Modal>
  )
}
